export type ServiceWorkerUpdateHandle = {
  /** Reload page (use currently waiting SW if any). */
  refreshToUpdate: () => void;
  /** Trigger skipWaiting on the waiting SW (if any), then reload. */
  updateNow: () => void;
};

export type ServiceWorkerUpdateListener = (handle: ServiceWorkerUpdateHandle) => void;

const UPDATE_READY_EVENT = 'edosar:sw-update-ready';

function emitUpdateReady(reg: ServiceWorkerRegistration) {
  const waiting = reg.waiting;
  const handle: ServiceWorkerUpdateHandle = {
    refreshToUpdate: () => {
      window.location.reload();
    },
    updateNow: () => {
      if (waiting) {
        waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      window.location.reload();
    },
  };
  window.dispatchEvent(new CustomEvent(UPDATE_READY_EVENT, { detail: handle }));
}

export function onServiceWorkerUpdateReady(listener: ServiceWorkerUpdateListener) {
  const fn = (e: Event) => {
    const ce = e as CustomEvent<ServiceWorkerUpdateHandle>;
    if (ce?.detail) listener(ce.detail);
  };
  window.addEventListener(UPDATE_READY_EVENT, fn);
  return () => window.removeEventListener(UPDATE_READY_EVENT, fn);
}

export async function registerServiceWorkerForProd() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });

      // If we already have a waiting worker, an update is ready.
      if (reg.waiting && navigator.serviceWorker.controller) {
        emitUpdateReady(reg);
      }

      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;

        installing.addEventListener('statechange', () => {
          // "installed" with an existing controller means there is an update ready to activate.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            emitUpdateReady(reg);
          }
        });
      });

      // Poll for updates (optional).
      setInterval(() => {
        reg.update().catch(() => undefined);
      }, 60_000);
    } catch (e) {
      // Swallow; SW is optional.
      // eslint-disable-next-line no-console
      console.log('Service Worker registration failed:', e);
    }
  });
}

